<!--
  פרטי העסק לחשבונאות - the popup that asks for the business's tax details (ח.פ / עוסק מורשה, נתוני
  חשבונאות, ספרור מסמכים) before the first accounting document. Mounted once in App.vue.

  Until 5/11/2026 an account without a ח.פ was bounced to /settings on every load (router/index.js)
  and Settings.vue opened its "ברוכים הבאים" popup on every visit - a new signup landed on the
  settings page under that popup and the starter kit's (the owner's signup review, item 3:
  "אחרי ההרשמה אין צעדים ראשונים - יש בקשה לנתוני מס"). Now the new account lands on its dashboard
  (the צעדים ראשונים card), and the details are asked for here:
    - on a finance screen only (FINANCE_PATHS: the documents, הנהלת חשבונות, the money pages);
    - while the business has neither a ח.פ nor an עוסק מורשה number (Settings' own rule: one of the
      two) and "אל תציג יותר אם גם חסרים פרטים" (businessSettings.stopDisplayingWelcomePopup) is off;
    - once per browser session (sessionStorage), whatever the answer;
    - for a user who can fill them in (the settings right) - never the root admin (its own login or
      inside an account), the reception desk, a frozen account;
    - never on top of another popup: it waits while a modal / an alert / the announcement / the
      "add to home screen" popup / a presentation is on screen, and while the starter kit is due.
  למילוי הפרטים → הגדרות → רשות המיסים (ח.פ, עוסק מורשה, ספרור מסמכים); אחר כך / × = not now.
  The checkbox keeps the old popup's meaning: ticked, the popup never comes back for the business.
-->
<template>

    <Modal ref="modal"
           :hideHeader="true"
           :hideFooter="true"
           :noClose="true">

        <template v-slot:body>

            <div class="taxDetailsPrompt">

                <button type="button"
                        class="btn-close taxDetailsPromptClose"
                        aria-label="סגירה"
                        title="אחר כך"
                        @click="later"></button>

                <div class="taxDetailsPromptIcon">
                    <i class="fa-solid fa-file-invoice"></i>
                </div>

                <h4 class="taxDetailsPromptTitle">לפני שמפיקים מסמכים חשבונאיים</h4>

                <p class="taxDetailsPromptSub">
                    על מנת שנוכל להפיק מסמכים חשבונאיים בשם העסק שלכם, אנחנו צריכים מספר נתונים לגביו:
                </p>

                <ul class="taxDetailsPromptList">
                    <li>
                        <span class="taxDetailsPromptItemIcon"
                              style="color:#ab8ce4"><i class="fa-solid fa-briefcase"></i></span>
                        פרטים כלליים של העסק
                    </li>
                    <li>
                        <span class="taxDetailsPromptItemIcon"
                              style="color:#00c292"><i class="fa-solid fa-file-lines"></i></span>
                        נתוני חשבונאות ורשות המיסים (ח.פ / עוסק מורשה)
                    </li>
                    <li>
                        <span class="taxDetailsPromptItemIcon"
                              style="color:#26c6da"><i class="fa-solid fa-list-ol"></i></span>
                        מספרי ספרור מסמכים חשבונאיים
                    </li>
                </ul>

                <label class="taxDetailsPromptDont"
                       @click="dontShowAgain = !dontShowAgain">
                    <span class="planup-checkbox planup-checkbox-purple"
                          :class="{ checked: dontShowAgain }">
                        <i class="fa fa-check"></i>
                    </span>
                    אל תציג יותר אם גם חסרים פרטים
                </label>

                <div class="taxDetailsPromptButtons">
                    <button type="button"
                            class="btn btn-primary taxDetailsPromptGo"
                            @click="goToSettings">
                        למילוי הפרטים
                    </button>
                    <button type="button"
                            class="btn btn-light taxDetailsPromptLater"
                            @click="later">
                        אחר כך
                    </button>
                </div>

            </div>

        </template>

    </Modal>

</template>

<script>
import { apiPost } from '../../core/http/axios_api_v2';
import hasPermission from '../../core/acl/acl';
import { isHostingStationUser } from '../../core/acl/venue';

// the finance screens (the side menu's כספים group): making / reading the documents, הנהלת
// חשבונות and the money pages. Path prefixes.
const FINANCE_PATHS = [
    '/accounting',
    '/createTaxDocument',
    '/taxDocument/',
    '/expected-payments',
    '/checks',
    '/expenses',
    '/reports/cashflow',
    '/reports/pnl',
    '/reports/taxDocumentReport',
];

const SESSION_KEY = 'planupTaxDetailsPrompt';

// the settings tab with ח.פ / עוסק מורשה, the bank accounts and the documents' numbering
const SETTINGS_TAB = '/settings?activeTab=orginizationSettings_reshutHamisimTab';

// a moment after the screen painted; a busy screen is asked again for about two minutes
const FIRST_SHOW_MS = 1200;
const RETRY_MS = 1500;
const MAX_TRIES = 80;

function isFinancePath(path) {
    let value = String(path || '');
    return FINANCE_PATHS.some(prefix => value.indexOf(prefix) === 0);
}

// the tab's session (sessionStorage); a browser that refuses the storage keeps it in memory until the
// next load instead
let shownInMemory = false;

function shownThisSession() {
    try {
        return sessionStorage.getItem(SESSION_KEY) == '1';
    } catch (e) {
        return shownInMemory;
    }
}

function rememberShown() {
    shownInMemory = true;
    try {
        sessionStorage.setItem(SESSION_KEY, '1');
    } catch (e) { }
}

// what the popup must not cover - the other popups' own list (AnnouncementPopup, HomeScreenPrompt,
// StarterKitModal) - and the starter kit while it is due (a new account's first load is the kit's)
function screenIsBusy(store) {

    if (document.querySelector('.modal.show'))
        return true;

    if (document.querySelector('.swal2-container.swal2-shown'))
        return true;

    if (document.querySelector('.puAnnOverlay, .homeScreenPrompt'))
        return true;

    // a presentation the customer is looking at (מצב תצוגה ללקוח)
    if (document.querySelector('.customerPresentation, .inventoryPresentation'))
        return true;

    if (document.querySelector('.accountFrozenModal'))
        return true;

    return !!(store.state.starterKit && store.state.starterKit.prompt);

}

export default {

    name: 'TaxDetailsPrompt',

    data() {
        return {
            open: false,
            dontShowAgain: false,
            timers: [],
        };
    },

    watch: {
        $route: {
            immediate: true,
            handler() {
                this.onRoute();
            },
        },
    },

    beforeDestroy() {
        this.clearTimers();
    },

    methods: {

        clearTimers() {
            this.timers.forEach(timer => clearTimeout(timer));
            this.timers = [];
        },

        after(fn, ms) {
            this.timers.push(setTimeout(fn, ms));
        },

        // a user who can fill the details in, on an account that lacks them
        eligible() {

            let state = this.$store.state;
            let user = state.user;

            if (!state.isLogged || !state.dashboardInitiated || !user || user.type == 1 || user.rootAdminSession)
                return false;

            if (isHostingStationUser(user) || state.accountFrozen || !hasPermission(user, 'settings.view'))
                return false;

            let settings = state.businessSettings || {};

            return !settings.hetPey && !settings.numOsekMurshe && !settings.stopDisplayingWelcomePopup;

        },

        due() {
            return !this.open && !shownThisSession() && isFinancePath(this.$route.path) && this.eligible();
        },

        onRoute() {

            this.clearTimers();

            // left the finance screen with the popup still open (the browser's back button)
            if (this.open && !isFinancePath(this.$route.path)) {
                this.close();
                return;
            }

            if (this.due())
                this.after(() => this.showWhenQuiet(0), FIRST_SHOW_MS);

        },

        showWhenQuiet(tries) {

            if (!this.due())
                return;

            if (screenIsBusy(this.$store)) {
                // still busy after ~2 minutes: the next finance screen asks again
                if (tries < MAX_TRIES)
                    this.after(() => this.showWhenQuiet(tries + 1), RETRY_MS);
                return;
            }

            this.show();

        },

        show() {

            rememberShown();
            this.dontShowAgain = false;
            this.open = true;

            if (this.$refs.modal)
                this.$refs.modal.show();

        },

        // ticked: the business is not asked again (the old popup's flag, saved the same way)
        close() {

            if (this.dontShowAgain) {
                this.$set(this.$store.state.businessSettings, 'stopDisplayingWelcomePopup', true);
                apiPost('business-settings', { stopDisplayingWelcomePopup: true }, true).catch(() => { });
            }

            this.open = false;

            if (this.$refs.modal)
                this.$refs.modal.hide();

        },

        goToSettings() {
            this.close();
            this.$router.push(SETTINGS_TAB).catch(() => { });
        },

        // אחר כך / the ×: not now - the next session asks again
        later() {
            this.close();
        },

    },

}
</script>

<style scoped>
.taxDetailsPrompt {
    position: relative;
    padding: 6px 6px 2px;
    text-align: center;
}

/* RTL: the × at the far (left) corner */
.taxDetailsPromptClose {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
}

.taxDetailsPromptIcon {
    width: 54px;
    height: 54px;
    margin: 4px auto 12px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 22px;
    background: linear-gradient(135deg, #7351c8, #8c6fd6);
    box-shadow: 0 6px 16px rgba(115, 81, 200, .28);
}

.taxDetailsPromptTitle {
    margin: 0 24px 8px;
    color: #2a3142;
    font-size: 21px;
    font-weight: 700;
}

.taxDetailsPromptSub {
    margin: 0 auto 14px;
    max-width: 380px;
    color: #6b7088;
    font-size: 15px;
    line-height: 1.5;
}

.taxDetailsPromptList {
    display: inline-block;
    margin: 0 0 16px;
    padding: 0;
    list-style: none;
    text-align: right;
}

.taxDetailsPromptList li {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 5px 0;
    color: #474d66;
    font-size: 15px;
}

.taxDetailsPromptItemIcon {
    flex: 0 0 26px;
    width: 26px;
    text-align: center;
    font-size: 19px;
}

.taxDetailsPromptDont {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin: 0 0 16px;
    color: #6b7088;
    font-size: 14px;
    cursor: pointer;
    user-select: none;
}

.taxDetailsPromptButtons {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    padding-bottom: 6px;
}

.taxDetailsPromptButtons .btn {
    min-width: 140px;
}

@media (max-width: 575px) {
    .taxDetailsPromptButtons .btn {
        flex: 1 1 100%;
    }
}
</style>
